import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { ArrowUpRight, BellRing, Compass, Inbox, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";

import { SKU } from "@/entities/pricing/model/priceBook";
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
// ★ 走 `@/contexts/AuthContext` 这个 shim 而不是 `@/features/auth/...`:
//   FSD 规矩是 features 互不 import(eslint no-restricted-imports 会当场拦),
//   跨 feature 的登录态只许经这道过渡门(ADR-0013 F1)。两个路径导出的是同一个 hook。
import { useAuth } from "@/contexts/AuthContext";
import { isTenantManagerPlus } from "@/shared/lib/rbac";
import { Button } from "@/shared/ui/button";
import { Pagination } from "@/shared/ui/pagination";
import { Skeleton } from "@/shared/ui/skeleton";
import { cn } from "@/shared/lib/utils";

import {
  ACQ_PRODUCT_NONE,
  acqListsRevision,
  acqService,
  runViaOf,
  subscribeAcqLists,
  TODAY_PAGE_SIZE,
  type AcqCatalogItem,
  type AcqOpportunityCard,
  type AcqTodayQuery,
  type AcqTodayView,
} from "../api/acqService";
import {
  CADENCE_LABEL,
  INTENT_TYPE_ORDER,
  intentTypeLabel,
  todayPickedText,
} from "../model/acqLabels";
import { useAcqActions } from "../model/useAcqActions";
import { discoverPriceText } from "../model/useDiscoverPrice";
import { useProductParam, withProduct } from "../model/useProductParam";
import { useTenantProducts } from "../model/useTenantProducts";
import { AcqShell } from "./AcqShell";
import { AcqPicker } from "./components/AcqPicker";
import { AssignControl } from "./components/AssignControl";
import { OpportunityCard } from "./components/OpportunityCard";
import { ProductTabs, type ProductTabItem } from "./components/ProductTabs";
import { StrategyCard } from "./components/StrategyCard";
import { errorText } from "@/shared/lib/apiErrors";

/**
 * 今日商机 —— **获客舱的默认落地页**(契约 §4 `GET /acq/today`)。
 *
 * 这一页不是"搜索结果页",是**今天该做的那几件事**。所以:
 *   ★ 文案一律说「跟」不说「打」:出口不止电话一种。0917 实测知乎提问者不可触达
 *     ⇒ 那条线改成内容线,出口是「今天该答的问题」。把这一页的话钉死成"三通电话",
 *     内容线接进来的那天就得满页改文案,而漏改的地方会静静地骗人。
 *     真正的动作由每张卡的 `suggestedAction` 说了算(见 `ACTION_SPEC` / `actionSpecFor`)。
 *   · 没有搜索框(契约 §9②:关键词搜索退到「按地图找」二级入口)
 *   · 没有仪表盘/报表(契约 §9③)
 *   · 排序与上限由服务端定,前端不复排(上限 = 按席位算的 `todayCap`,契约 §13.1)。
 *
 * ★ 0917 批次二(契约 §13.6):顶部多了三个筛子(全部 / 只看我的 / 按产品)与分页。
 *   **分页不是"加载更多"**:上限还是上限,分页只是让一个十几人的团队能把今天这批翻完
 *   —— "再给我 50 条"正是上一版把人逼停的东西(新视线 108 次搜索 216 积分 0 线索)。
 */
/** 反馈后停多久再重拉今日页:够他看见按钮亮了,又不至于以为没反应。 */
const FEEDBACK_SETTLE_MS = 1200;

export interface TodayPageProps {
  /** `/ai-huoke` 的落地分流已经拉过一次,递进来就别再拉第二遍(首屏少一跳)。 */
  initialToday?: AcqTodayView | null;
  /**
   * 目录行,用来在右栏说「已订阅 N 种」并在空态分两种说法。
   * `undefined` = 这一次没人告诉我(直接渲染这个组件的测试/桩),右栏就只给「去目录」链接,
   * **不自己去拉一遍目录** —— 那会让这一页多一个它本不需要的依赖。
   */
  catalogItems?: AcqCatalogItem[] | null;
}

export default function TodayPage({ initialToday, catalogItems }: TodayPageProps = {}) {
  const [view, setView] = useState<AcqTodayView | null>(initialToday ?? null);
  const [error, setError] = useState<string | null>(null);
  const [reloading, setReloading] = useState(false);
  const [page, setPage] = useState(1);
  /**
   * 范围(1027 归属分权):后端已把列表钳在「我的范围 + 公共池」里,这三档只是在里面再收窄。
   * 「公共池」= 没人认的(夜跑 / 代客 / 1027 之前的老卡),谁先认领归谁。
   */
  const [range, setRange] = useState<"all" | "mine" | "pool">("all");
  const mine = range === "mine";
  /**
   * 按产品看(契约 §16.2 / §16.7)——**页签,不是下拉**,而且跟 URL 走。
   * ★ 上一版是筛子里的一档:它与「意向」「只看我的」并排,看起来是三个同等的筛子;
   *   而产品是这一舱的主轴,四页要能带着它互相跳。所以它升成页签并进 URL。
   */
  const [productCode, setProductCode] = useProductParam();
  const [intentType, setIntentType] = useState("");
  /**
   * 落地分流(`AcqLanding`)已经拉过一次就别再拉第二遍(首屏少一跳)。
   *
   * ★★ 0918 批次八抓到的(真渲染):`AcqLanding` 拉的那一份是 `acqService.today()` ——
   *   **不带任何筛子**。于是深链 `/ai-huoke?product=X`(分享出去的地址、刷新、后退)
   *   会把那份**全量**列表当成"X 这条产品线的今天",页签亮着 X、列表里却是所有产品的卡,
   *   而页面一个错都不报。所以 URL 上带了产品时**不认这个种子**,老老实实自己拉一次
   *   ——只有这一种情况多一跳。
   */
  const seeded = useRef(initialToday !== undefined && !productCode);
  const { book } = usePriceBook();
  const listPrice = book.unit(SKU.acqUnlock) ?? book.unit(SKU.acqUnlock, "default");

  // ★ 角色与「我是谁」都从 useAuth 取,与后端同口径(TENANT_ADMIN / TENANT_MANAGER 可分派)。
  //   `effectiveRole` 而不是 `user.role`:平台超管代客进来时后端把视图角色切成了 TENANT_ADMIN。
  const { user, effectiveRole } = useAuth();
  const canAssign = isTenantManagerPlus(effectiveRole);
  const meId = user?.userId ?? null;
  const { products, nameOf } = useTenantProducts();

  /**
   * 1023:点了反馈(成了 / 没成 / 找错人 / 空号 / 再跟进)的卡已经不属于今日页了 —— 先就地替换让他看到
   * 按钮亮了,停一下再重拉:这张卡离开、后面的补上来、页签数与「已满」一起对上。此前只就地替换,
   * 得手动刷新才对得上。★ 接手**不**触发重拉:产物面板就在这张卡上,他正要复制。
   */
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reloadSoonRef = useRef<() => void>(() => {});
  useEffect(
    () => () => {
      if (reloadTimer.current) clearTimeout(reloadTimer.current);
    },
    []
  );
  const patch = useCallback((c: AcqOpportunityCard) => {
    let leaving = false;
    setView((prev) => {
      if (!prev) return prev;
      const before = prev.items.find((x) => x.id === c.id);
      leaving = !!c.feedbackCode && before?.feedbackCode !== c.feedbackCode;
      return { ...prev, items: prev.items.map((x) => (x.id === c.id ? c : x)) };
    });
    if (leaving) reloadSoonRef.current();
  }, []);
  const { busyId, take, unlock, feedback, takeAction, assign, claim } = useAcqActions(patch);

  /**
   * 「列表旧了」的信号(契约 §14.9 重判补绑):它改的是这一页每张卡的产品格与意向格,
   * 而触发在**我的生意**页。把版本号读进 query 依赖 ⇒ 这一页挂着的时候当场重取。
   * ★ 这一舱没走 react-query(没有跨页缓存),平时用户走过来本来就是重新拉的;
   *   这一行补的是"两页同时挂着"那一格。
   */
  const listsRev = useSyncExternalStore(subscribeAcqLists, acqListsRevision, () => 0);

  const query = useMemo<AcqTodayQuery>(
    () => ({
      page,
      size: TODAY_PAGE_SIZE,
      mine,
      ...(range === "pool" ? { scope: "pool" as const } : {}),
      productCode,
      intentType,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- listsRev 是"重取一次"的信号,不进 query body
    [page, range, productCode, intentType, listsRev]
  );

  const load = useCallback(async (q: AcqTodayQuery) => {
    setReloading(true);
    try {
      setView(await acqService.today(q));
      setError(null);
    } catch (e) {
      setError(errorText(e, { fallbackTitle: "加载失败" }));
    } finally {
      setReloading(false);
    }
  }, []);

  reloadSoonRef.current = () => {
    if (reloadTimer.current) clearTimeout(reloadTimer.current);
    reloadTimer.current = setTimeout(() => void load(query), FEEDBACK_SETTLE_MS);
  };

  useEffect(() => {
    if (seeded.current) {
      seeded.current = false;
      return;
    }
    void load(query);
  }, [load, query]);

  const cards = view?.items ?? null;
  const count = cards?.length ?? 0;
  const total = view?.total ?? count;
  const filtered = range !== "all" || !!productCode || !!intentType;
  /** 除产品之外还开着的筛子 —— 空态与副标题的说法都按它分岔(见 `EmptyToday` 里那段)。 */
  const otherFilters = range !== "all" || !!intentType;
  /**
   * 产品页签的数据(契约 §16.2):优先用服务端的 `productSummary`(今日数),
   * 后端还没跟上就退到产品树 —— 名字有、**数不报**(报一个前端自己数的今日数
   * 只会与分页后的那一页对不上,而用户是照着它去点的)。
   */
  const tabs = useMemo<ProductTabItem[]>(() => {
    const summary = view?.productSummary;
    if (summary && summary.length > 0) {
      return summary.map((s) => ({ code: s.code, name: s.name, count: s.todayCount }));
    }
    return products.map((p) => ({ code: p.nodeCode, name: p.name, count: null }));
  }, [view, products]);
  /**
   * 选中页签那个产品的名字(契约 §19.3 的空态要把它写进那句话里)。
   * ★ 先查页签那一排(后端 `productSummary` 给的名字),再退到产品树 —— `nameOf` 查不到时
   *   回落成 code 本身,所以这一格**永远不是空字符串**(空的话那句话会变成「「」还没有商机」)。
   * ★ 「全部」与哨兵 `__none__` 都不算"一个产品":前者没得说,后者没有词、没有场景,
   *   「按这个产品发现一次」对它不成立(与 `ProductTabs` 的 `allowNone=false` 同一条理由)。
   */
  const focusProduct = useMemo(() => {
    if (!productCode || productCode === ACQ_PRODUCT_NONE) return null;
    const name = tabs.find((t) => t.code === productCode)?.name ?? nameOf(productCode);
    return { code: productCode, name };
  }, [productCode, tabs, nameOf]);
  const subscribed = useMemo(
    () => (catalogItems ?? []).filter((i) => i.subscribed),
    [catalogItems]
  );
  const hiddenCount = useMemo(
    () => (catalogItems ?? []).filter((i) => i.hidden).length,
    [catalogItems]
  );
  /**
   * 空态的头三张迷你卡:主推 / 可试里能跑的前三种。
   * ★ 不给"全部 17 种"—— 空态要给的是**下一个动作**,不是又一张目录。
   * ★ 用户藏起来的那几种不当起点:他刚说过不看它。
   */
  const starters = useMemo(
    () =>
      (catalogItems ?? [])
        .filter(
          (i) =>
            i.status === "ACTIVE" &&
            // ★ 能当场跑一次的就行:`ai` 与 `api`(第一方意向)都算,只有 `link` 型不算
            //   —— 它的出口是别的页面,不是「发现一次」。大小写归一见 `runViaOf`。
            runViaOf(i) !== "link" &&
            !i.hidden &&
            (i.level === "P0" || i.level === "P1")
        )
        .slice(0, 3),
    [catalogItems]
  );
  const subscribedCount = catalogItems == null ? null : subscribed.length;
  /**
   * ★ 副标题必须和空态说**同一件事**。真渲染时抓到的:一个还没订阅过任何一种的用户,
   *   页面主体写「先去挑一个地方找」,而副标题在跟他说「筛过了」——
   *   什么都没跑过,哪来的"筛"?这种自相矛盾的一页会让人以为系统在糊弄他。
   * ★ 筛过之后说的是**另一件事**:0 条不代表今天没单子,只代表这个筛子下没有。
   *   把两者混成一句,用户会以为自己把今天的单子弄丢了。
   */
  /**
   * ★★ 0918 批次八真渲染抓到的:选中一个 0 条的产品页签时,主体写「「X」还没有商机」
   *   而副标题在跟他说「这个筛子下没有 —— 换个筛子看看」—— 同一屏上两句话指向两件事
   *   (一个说"去找一次",一个说"换个筛子"),用户会照着更省事的那句走,然后什么也没发生。
   *   所以产品这一档单独出一句。
   */
  const subtitle =
    view == null
      ? "正在拉今天的单子…"
      : total > 0
        ? focusProduct && !otherFilters
          ? `「${focusProduct.name}」今天有 ${total} 条`
          : filtered
            ? `这个筛子下有 ${total} 条`
            : todayPickedText(total, cards)
        : focusProduct && !otherFilters
          ? `「${focusProduct.name}」还没有商机 —— 按这个产品找一次就有了`
          : filtered
            ? "这个筛子下没有 —— 换个筛子看看"
            : subscribedCount === 0
              ? "还没开始找 —— 挑一个地方跑一次就有了"
              : "今天没有值得跟的 —— 这不是故障，是筛过了";

  return (
    <AcqShell
      subtitle={subtitle}
      actions={
        // ★ 0917 v2:**「立即采集」从这一页拿掉了**。发现现在按种类跑、按种类收费
        //   (契约 §12.4 `POST /discover/run` 的 body `{type}` 必填),
        //   一个不说明"跑哪一种、扣多少分"的按钮没法诚实地摆在这里 —— 它的新家是目录页。
        <Button variant="outline" size="sm" onClick={() => void load(query)} disabled={reloading}>
          <RefreshCw className={reloading ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
          刷新
        </Button>
      }
    >
      {/* ★★ 两栏(契约 §12.8):左边是今天的单子,右边是「这周押什么」+ 订阅摘要。
          `max-w-3xl` 删掉了 —— 上一版把一页宽屏挤在左三分之一里,右边三分之二全是空白,
          而"这周该押哪条线"这件事本来就该和单子摆在一起看,不该藏在另一个页面。
          `minmax(0,1fr)` 而不是 `1fr`:不写 minmax,长公司名会把左列顶宽、右列被挤没。 */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-4">
          {/* ── 产品页签(契约 §16.2 / §16.7):主轴在最上面,筛子在它下面 ──────
              ★ 它与下面那排筛子**不是一档东西**:页签换的是"在看哪条产品线",
              筛子换的是"在这条线里看哪一批"。摆成同一排的话,用户会把产品当成
              第四个筛子 —— 而老板要的正好相反:「我们这个仓应该都是根据产品来驱动的」。 */}
          <ProductTabs
            items={tabs}
            value={productCode}
            onChange={(code) => {
              setProductCode(code);
              setPage(1);
            }}
            countLabel="条今天"
            // ★ 今日是**筛卡**的页面,所以「未指定产品」在这儿是真的一档
            //   (送哨兵 `__none__`,契约 §16.2 api 落地形状)。
            allowNone
          />

          {/* ── 筛子:全部 / 只看我的 / 意向(契约 §13.6)────────────────── */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-lg border p-0.5" role="group" aria-label="范围">
              {(
                [
                  { on: "all", label: "全部" },
                  { on: "mine", label: "只看我的" },
                  { on: "pool", label: "公共池" },
                ] as const
              ).map((seg) => (
                <button
                  key={seg.label}
                  type="button"
                  aria-pressed={range === seg.on}
                  onClick={() => {
                    setRange(seg.on);
                    setPage(1);
                  }}
                  className={cn(
                    "rounded-md px-2.5 py-1 text-xs font-semibold transition-colors",
                    range === seg.on
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {seg.label}
                </button>
              ))}
            </div>
            {/* 0917 批次三(契约 §14.3):「今天只想接问价的」—— 意向与产品、只看我的正交。 */}
            <AcqPicker
              label="意向"
              value={intentType}
              onChange={(v) => {
                setIntentType(v);
                setPage(1);
              }}
              options={INTENT_TYPE_ORDER.map((c) => ({ value: c, label: intentTypeLabel(c) }))}
            />
            {filtered && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setRange("all");
                  setProductCode("");
                  setIntentType("");
                  setPage(1);
                }}
              >
                清空筛选
              </Button>
            )}
          </div>

          {error && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              {error}
              <button
                type="button"
                onClick={() => void load(query)}
                className="ml-2 font-semibold underline"
              >
                重试
              </button>
            </div>
          )}

          {view == null && !error && (
            <>
              <Skeleton className="h-64 w-full rounded-xl" />
              <Skeleton className="h-64 w-full rounded-xl" />
            </>
          )}

          {view != null && count === 0 && (
            <EmptyToday
              subscribedCount={subscribedCount}
              starters={starters}
              cap={view.cap}
              hiddenCount={hiddenCount}
              filtered={filtered}
              product={productCode}
              focusProduct={focusProduct}
              // ★ 「除了产品之外还开着别的筛子」—— 见 `EmptyToday` 里那段优先级注释。
              otherFilters={otherFilters}
              onPickAll={() => {
                setProductCode("");
                setPage(1);
              }}
              onClearFilters={() => {
                setRange("all");
                setProductCode("");
                setIntentType("");
                setPage(1);
              }}
            />
          )}

          {cards?.map((card, i) => (
            <OpportunityCard
              key={card.id}
              card={card}
              rank={(page - 1) * TODAY_PAGE_SIZE + i + 1}
              unlockPrice={card.unlockCredits ?? listPrice}
              busy={busyId === card.id}
              onTake={take}
              onUnlock={unlock}
              onFeedback={feedback}
              onAction={takeAction}
              // 契约 §16.7:卡上的产品 chip 点一下就切到那个产品的页签(并回第一页)。
              onPickProduct={(code) => {
                setProductCode(code);
                setPage(1);
              }}
              assignSlot={
                <AssignControl
                  card={card}
                  canAssign={canAssign}
                  meId={meId}
                  busy={busyId === card.id}
                  onAssign={(id, aid, name) => void assign(id, aid, name)}
                  onClaim={(id) => void claim(id)}
                />
              }
            />
          ))}

          {view != null && count > 0 && (
            <>
              {/* ★ 0917 批次四(契约 §15.3):共用 `@/shared/ui/pagination`,与线索库、
                  客服会话页同一套。`page` 这边 1-based、组件 0-based,进出各错一位。 */}
              {total > TODAY_PAGE_SIZE && (
                <Pagination
                  page={page - 1}
                  pageSize={TODAY_PAGE_SIZE}
                  total={total}
                  disabled={reloading}
                  onPageChange={(p) => setPage(p + 1)}
                />
              )}
              <p className="pt-1 text-center text-2xs text-muted-foreground">
                {/* ★ cap 是今日上限的回显:没它,用户看到 3 张卡不知道是「只有这三条」
                    还是「被截断了」—— 那正是他会回来一遍遍刷新的理由。 */}
                每天最多 {view.cap} 条（按你的在职席位数定），早 9 点自动送到，不用回来刷。
                <Link
                  // 契约 §16.7:互跳带着产品 —— 正在看某条产品线的人点「看全部线索」,
                  // 要的是那条线的全部,不是一页混在一起的表。
                  to={withProduct("/ai-huoke/leads", productCode)}
                  className="ml-1 font-semibold text-primary hover:underline"
                >
                  看全部线索
                </Link>
              </p>
            </>
          )}
        </div>

        {/* ★ 右栏在 lg 以下整列掉到单子下面(grid 默认单列),窄屏不会被挤成一条竖缝。 */}
        <aside className="min-w-0 space-y-4">
          <StrategyCard />
          <SubscriptionSummary items={subscribed} known={catalogItems != null} />
        </aside>
      </div>
    </AcqShell>
  );
}

/**
 * 空态。**四种说法**,因为它们要人做的事完全不同:
 *   · 某个产品页签下 0 张    → 「按这个产品发现一次」(契约 §19.3,0918 批次八新增);
 *   · 别的筛子筛空了        → 「换个筛子」,别的什么都不说;
 *   · 还没订阅任何一种      → 「先去挑一个地方找」+ 三张能当场跑一次的迷你卡;
 *   · 订阅了但今天没够格的  → 「筛过了」,策略卡照显。
 * ★ 把这几种合成一句「今天没有商机」,对前三种用户等于什么也没说 ——
 *   一个是自己把筛子设窄了,一个根本没开始,而页面在跟他们解释"为什么今天没有"。
 */
function EmptyToday({
  subscribedCount,
  starters,
  cap,
  hiddenCount,
  filtered,
  product,
  focusProduct,
  otherFilters,
  onPickAll,
  onClearFilters,
}: {
  subscribedCount: number | null;
  starters: AcqCatalogItem[];
  cap: number;
  hiddenCount: number;
  filtered: boolean;
  /** 选中的产品页签:去目录的几条路都带着它(契约 §16.7)。 */
  product: string;
  /** 选中的那个**具体**产品(码 + 名字)。「全部」与「未指定产品」都是 null。 */
  focusProduct: { code: string; name: string } | null;
  /** 除了产品之外还开着「只看我的」/「意向」吗。 */
  otherFilters: boolean;
  /** 「看全部产品」—— 只松产品这一档,别的筛子留着。 */
  onPickAll: () => void;
  onClearFilters: () => void;
}) {
  /**
   * ★★ 0918 批次八(契约 §19.3):**产品页签下的 0 条要给出口**。
   *   老板看到的就是这一屏:「销帮AI智脑」「阳光400电话」点进去空空如也,
   *   而页面只说「这个筛子下没有」—— 那句话把"这条产品线还没找过"说成了"筛子的事",
   *   于是他下一步无处可去。这一版直接给那一下:按这个产品去发现一次。
   * ★ **只在产品是唯一的筛子时**这么说。还开着「只看我的」/「意向」的时候,
   *   0 条的原因很可能是"都派给别人了" —— 那时劝他花钱再找一次是错的建议。
   */
  if (focusProduct && !otherFilters) {
    return (
      <div className="rounded-xl border border-dashed bg-card px-6 py-10 text-center">
        <Compass className="mx-auto h-8 w-8 text-primary" />
        <p className="mt-3 text-base font-bold">「{focusProduct.name}」还没有商机</p>
        <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted-foreground">
          {/* ★ 不说"这条线没客户"——说的是**我们还没为它找过**。这两句给人的下一步完全不同。 */}
          {/* ★ 整句写在一个字符串里:JSX 里断行会在句中留下一个空格
              (真渲染看得见「发现一次， 找回来」中间那道缝)。 */}
          {
            "这条产品线今天一条都没有。去「去哪里找」按这个产品发现一次，找回来的卡会直接落在这个页签下。"
          }
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <Button asChild size="sm">
            <Link to={withProduct("/ai-huoke/discover", focusProduct.code)}>
              <Compass className="h-4 w-4" />
              按「{focusProduct.name}」发现一次
            </Link>
          </Button>
          {/* ★ 「看全部产品」松的是产品这一档,不是"清空筛选" —— 这一支里本来就没有别的筛子。 */}
          <Button variant="outline" size="sm" onClick={onPickAll}>
            看全部产品
          </Button>
        </div>
        <CapNote cap={cap} hiddenCount={hiddenCount} />
      </div>
    );
  }

  if (filtered) {
    return (
      <div className="rounded-xl border border-dashed bg-card px-6 py-10 text-center">
        <Inbox className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-3 text-sm font-semibold">这个筛子下没有</p>
        <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted-foreground">
          今天的单子可能都派给别人了，或者不在这个产品线上。
        </p>
        <Button variant="outline" size="sm" className="mt-4" onClick={onClearFilters}>
          看全部
        </Button>
      </div>
    );
  }

  const neverStarted = subscribedCount === 0;

  if (neverStarted) {
    return (
      <div className="rounded-xl border border-dashed bg-card px-6 py-10 text-center">
        <Compass className="mx-auto h-8 w-8 text-primary" />
        <p className="mt-3 text-base font-bold">今天还没有新商机</p>
        <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted-foreground">
          商机得先找回来才会出现在这儿。去「去哪里找」挑一个地方发现一次，两三分钟；一条都没找到的那次不扣分。
        </p>

        {starters.length > 0 && (
          // ★ flex 居中而不是 `sm:grid-cols-3`:推荐档里能跑的常常不足三种
          //   (link 型和 COMING_SOON 都进不来),固定三列时两张卡会贴左、右边空出一格,
          //   看起来像"还有一张没加载出来"。真渲染时抓到的。
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {starters.map((s) => (
              <Link
                key={s.type}
                to={withProduct("/ai-huoke/discover", product)}
                className="min-w-0 flex-1 basis-48 rounded-lg border bg-background px-3 py-2.5 text-left transition-colors hover:border-primary hover:bg-primary/5 sm:max-w-xs"
              >
                <span className="block truncate text-xs font-semibold">{s.name}</span>
                <span className="mt-1 block text-2xs font-semibold text-primary">
                  发现一次 · {discoverPriceText(s.priceCredits ?? null)}
                </span>
              </Link>
            ))}
          </div>
        )}

        <Button asChild size="sm" className="mt-4">
          <Link to={withProduct("/ai-huoke/discover", product)}>
            <Compass className="h-4 w-4" />
            去发现商机
          </Link>
        </Button>
        <CapNote cap={cap} hiddenCount={hiddenCount} />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-dashed bg-card px-6 py-12 text-center">
      <Inbox className="mx-auto h-8 w-8 text-muted-foreground" />
      <p className="mt-3 text-sm font-semibold">今天没有够格的商机</p>
      <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted-foreground">
        宁可今天空着，也不拿凑数的名单占你时间。想扩大范围就去目录里多挑一个地方找，
        或者去「按地图找」自己挑。
      </p>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        <Button asChild size="sm">
          <Link to={withProduct("/ai-huoke/discover", product)}>
            <BellRing className="h-4 w-4" />
            去哪里找
          </Link>
        </Button>
        <Button asChild size="sm" variant="outline">
          <Link to="/ai-huoke/map">按地图找</Link>
        </Button>
      </div>
      <CapNote cap={cap} hiddenCount={hiddenCount} />
    </div>
  );
}

/**
 * 空态底下那句「上限多少、藏了几种」。
 * ★ 上限要在**空着的时候**就说清楚:一个今天看到 0 条的人最该知道的是
 *   "这不是因为上限卡着我",而「已隐藏 N 种」则是他自己可能忘了的那一刀
 *   (契约 §13.1 补漏:今日页空态与目录同步显示「已隐藏 N 种」)。
 * ★ 席位数不反推,只报服务端给的 cap。
 */
function CapNote({ cap, hiddenCount }: { cap: number; hiddenCount: number }) {
  return (
    <p className="mx-auto mt-3 max-w-md text-2xs leading-relaxed text-muted-foreground">
      今日上限 {cap} 条（按你的在职席位数定）。想多看，去目录多订几种。
      {hiddenCount > 0 && (
        <>
          {" "}
          你还
          <Link
            to="/ai-huoke/discover"
            className="mx-0.5 font-semibold text-primary hover:underline"
          >
            隐藏了 {hiddenCount} 种
          </Link>
          ，它们不会再出单子。
        </>
      )}
    </p>
  );
}

/**
 * 右栏的订阅摘要。
 * ★ `known=false`(没人把目录递进来)时**只给入口,不报数** —— 报一个猜的 0
 *   会让一个其实订了三种的人以为自己的订阅掉了。
 */
function SubscriptionSummary({ items, known }: { items: AcqCatalogItem[]; known: boolean }) {
  return (
    <section className="rounded-xl border bg-card px-4 py-3">
      <p className="text-sm font-bold">在自动找的</p>
      {known ? (
        items.length === 0 ? (
          <p className="mt-1 text-2xs leading-relaxed text-muted-foreground">
            还没有。手动跑过觉得有用的那种，去目录里把「订阅自动找」打开就行。
          </p>
        ) : (
          <>
            <p className="mt-1 text-2xs text-muted-foreground">已订阅 {items.length} 种</p>
            <ul className="mt-1.5 space-y-1">
              {items.map((i) => (
                <li key={i.type} className="flex items-center justify-between gap-2 text-xs">
                  <span className="min-w-0 truncate">{i.name}</span>
                  <span className="shrink-0 text-2xs text-muted-foreground">
                    {CADENCE_LABEL[i.cadence]}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )
      ) : (
        <p className="mt-1 text-2xs leading-relaxed text-muted-foreground">
          去目录里看你都订了哪几种。
        </p>
      )}
      <Link
        to="/ai-huoke/discover"
        className="mt-2.5 inline-flex items-center gap-0.5 text-2xs font-semibold text-primary hover:underline"
      >
        去目录
        <ArrowUpRight className="h-3 w-3" />
      </Link>
    </section>
  );
}
